import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../auth/context/AuthProvider";
import { useCompanyContext } from "../../common/company/CompanyContext";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchCorporateEntity, fetchCorporateHierarchy } from "../corporateInteractionService";
import { fetchAddressByOwner } from "../addressInteractionService";
import { fetchCompanyTaxonomy } from "../companyTaxonomyInteractionService";
import { fetchTaxonomyHeaders } from "../../site-admin/components/Workflow-esg/services/EsgTaxonomySyncService";

/**
 * 🎯 COMPANY DASHBOARD
 * Landing page for a user working within a single active company (set via
 * the dashboard's company icon). Shows who's using it, the company's own
 * name/address, its recorded hierarchy, and its connected ESG taxonomy —
 * then hands off to the existing management screens (Structure/Suppliers/
 * Taxonomy tabs on Corporate Details, Data-Entry for bulk insert) rather
 * than duplicating them here.
 */
export default function CompanyDashboardView() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { activeCompanyId, activeCompanyName } = useCompanyContext();

  const [entity, setEntity] = useState(null);
  const [address, setAddress] = useState(null);
  const [hierarchy, setHierarchy] = useState([]);
  const [taxonomyLabel, setTaxonomyLabel] = useState(null);
  const [loading, setLoading] = useState(true);

  const siteKey = process.env.REACT_APP_SITE_KEY;
  const SITE_NAME = process.env.REACT_APP_SITE_NAME || "sustina.nrgpix.ch";

  const load = useCallback(async () => {
    if (!activeCompanyId) return;
    setLoading(true);
    try {
      const entityData = await fetchCorporateEntity(activeCompanyId, { siteKey });
      setEntity(entityData);

      const entityGuid = GoldenSourceHelper.get(entityData, "CorporateEntityGuid");
      if (entityGuid) {
        const addressData = await fetchAddressByOwner(entityGuid, { siteKey });
        setAddress(addressData);
      }

      const tree = await fetchCorporateHierarchy(activeCompanyId, { siteKey });
      setHierarchy(tree || []);

      const binding = await fetchCompanyTaxonomy(activeCompanyId, { siteKey });
      const boundHeaderId = GoldenSourceHelper.get(binding, "HeaderId");
      if (boundHeaderId) {
        const headers = await fetchTaxonomyHeaders({ siteKey });
        const match = headers.find((h) => GoldenSourceHelper.get(h, "HeaderId") === boundHeaderId);
        setTaxonomyLabel(
          match
            ? `${GoldenSourceHelper.get(match, "VersionLabel")} (${GoldenSourceHelper.get(match, "TaxYear")})`
            : boundHeaderId
        );
      } else {
        setTaxonomyLabel(null);
      }
    } finally {
      setLoading(false);
    }
  }, [activeCompanyId, siteKey]);

  useEffect(() => {
    if (!activeCompanyId) {
      navigate("/registered/dashboard");
      return;
    }
    load();
  }, [activeCompanyId, load, navigate]);

  const handleSignOut = async () => {
    await logout();
    navigate("/auth/sign-in");
  };

  if (!activeCompanyId || loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status"></div>
          <p className="text-muted fw-bold">Loading company dashboard...</p>
        </div>
      </div>
    );
  }

  const companyName = GoldenSourceHelper.get(entity, "EntityName") || activeCompanyName;
  const companyNumber = GoldenSourceHelper.get(entity, "TaxReference") || GoldenSourceHelper.get(entity, "RegistrationNumber");
  const addressLine = address
    ? [
        GoldenSourceHelper.get(address, "Address_Line1"),
        GoldenSourceHelper.get(address, "Address_Line2"),
        [GoldenSourceHelper.get(address, "Address_Town"), GoldenSourceHelper.get(address, "Address_County")].filter(Boolean).join(", "),
        [GoldenSourceHelper.get(address, "Postal_Code"), GoldenSourceHelper.get(address, "Country_ISO2")].filter(Boolean).join(" "),
      ].filter(Boolean).join(", ")
    : "No registered address on file";

  return (
    <main id="main" className="page-main" data-scroll-container>
      <div className="page-main-inner">
        <section className="page-section">
          <div className="container mt-4">
            <div className="row mb-4">
              <div className="col">
                <h2 className="h4 fw-bold mb-1">{companyName}</h2>
                <p className="text-muted extra-small mb-1">
                  <i className="bi bi-geo-alt me-1"></i>{addressLine}
                  {companyNumber && <span className="ms-2">&middot; Reg No. {companyNumber}</span>}
                </p>
                <p className="text-muted small mb-0">
                  Signed in as <strong>{user?.fullName || user?.userName || user?.email || "User"}</strong>
                </p>
              </div>
            </div>

            <div className="card shadow-sm border-0 p-3 mb-4">
              <div className="row g-3">
                {/* TAXONOMY */}
                <div className="col-12 col-md-6">
                  <h6 className="extra-small fw-bold text-uppercase text-muted mb-1">
                    <i className="bi bi-diagram-2 me-1"></i> ESG Taxonomy
                  </h6>
                  {taxonomyLabel ? (
                    <p className="extra-small fw-bold mb-0">{taxonomyLabel}</p>
                  ) : (
                    <p className="extra-small text-muted italic mb-0">No taxonomy connected yet.</p>
                  )}
                </div>

                {/* HIERARCHY */}
                <div className="col-12 col-md-6">
                  <h6 className="extra-small fw-bold text-uppercase text-muted mb-1">
                    <i className="bi bi-diagram-3 me-1"></i> Corporate Structure
                  </h6>
                  {hierarchy.length > 0 ? (
                    <p className="extra-small text-muted mb-0">
                      {hierarchy.map((node, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && ", "}
                          {GoldenSourceHelper.get(node, "EntityName")}
                        </React.Fragment>
                      ))}
                    </p>
                  ) : (
                    <p className="extra-small text-muted italic mb-0">No hierarchy recorded yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* ACTIONS — everything moved off the top-level dashboard lives
                here instead, one level deeper, scoped to the active company */}
            <div className="row g-2 mb-4">
              {[
                { label: "Manage Child Companies", icon: "bi-diagram-3", onClick: () => navigate(`/registered/represent-company?parentId=${activeCompanyId}`) },
                { label: "Accounts & Ledger", icon: "bi-journal-text", onClick: () => navigate("/registered/company-accounts") },
                { label: "HR & Workforce", icon: "bi-people", onClick: () => navigate("/registered/company-hr") },
                ...(String(process.env.REACT_APP_SSO_SIGNIN || "").toLowerCase() === "true"
                  ? [{ label: "Single Sign-On", icon: "bi-shield-lock", onClick: () => navigate("/registered/company-sso") }]
                  : []),
                { label: "Company ESG Scope", icon: "bi-diagram-2", onClick: () => navigate("/registered/company-scope") },
                { label: "Chart of Accounts", icon: "bi-list-columns", onClick: () => navigate("/registered/company-chart-of-accounts") },
                { label: "Attribute Groups", icon: "bi-collection", onClick: () => navigate("/registered/declr-attribute-groups") },
                { label: "Local Declaration", icon: "bi-pencil-square", onClick: () => navigate("/registered/declr-local-declaration") },
                { label: "Suppliers", icon: "bi-truck", onClick: () => navigate("/registered/suppliers") },
                { label: "Bulk Insert", icon: "bi-upload", onClick: () => navigate("/registered/data-entry") },
                { label: "Evidences", icon: "bi-camera", onClick: () => navigate("/capabilities/uploads") },
                { label: "Monitoring", icon: "bi-camera-reels", onClick: () => navigate("/capabilities/monitoring") },
                { label: "Draft and Reviews", icon: "bi-graph-up", onClick: () => navigate("/capabilities/reviews") },
                { label: "Commentaries & Publication", icon: "bi-mic", onClick: () => navigate("/capabilities/commentaries") },
              ].map((action) => (
                <div key={action.label} className="col-6 col-md-3 col-lg-3">
                  <div
                    className="card h-100 shadow-sm border dashboard-card"
                    onClick={action.onClick}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-body d-flex align-items-center gap-2 py-2 px-3">
                      <i className={`bi ${action.icon} h4 mb-0`}></i>
                      <span className="extra-small fw-bold text-uppercase">{action.label}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <div className="d-flex gap-3">
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate("/registered/dashboard")}>
                  <i className="bi bi-arrow-left me-2"></i> Back to Dashboard
                </button>
                <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={handleSignOut}>
                  <i className="bi bi-box-arrow-left me-2"></i> Sign Out
                </button>
              </div>
              <div className="text-end">
                <span className="text-muted extra-small d-block">Workflow Context</span>
                <span className="fw-bold small">{companyName || SITE_NAME}</span>
              </div>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
}
